import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ShieldCheckIcon, 
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import { verifyCertificate } from "../../api/certificateApi.js";

const VerifyCertificate = () => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certData, setCertData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const verify = async () => {
      try {
        const data = await verifyCertificate(serial);
        if (isMounted) setCertData(data?.certificate || null);
      } catch (err) {
        if (isMounted) setCertData(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    verify();
    return () => { isMounted = false; };
  }, [serial]);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-[#020617] text-white flex flex-col items-center justify-center p-4">
      {/* Glow Effect */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-md w-full relative">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/30 to-emerald-500/30 rounded-[2rem] blur-xl opacity-50"></div>
        
        <div className="relative bg-[#0a0a0a]/90 border border-white/10 rounded-[2rem] backdrop-blur-3xl shadow-2xl overflow-hidden">
          
          {certData ? (
            <div className="p-8">
              {/* TOP ROW: LOGO AND VERIFICATION STATUS */}
              <div className="flex justify-between items-start mb-12">
                {/* Top Left: Brand Logo */}
                <div className="flex items-center space-x-2">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-gray-800 to-black border border-white/10 flex items-center justify-center font-black shadow-lg">
                    <span className="text-xs text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-500">GZ</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[12px] font-black tracking-tighter italic italic ">GenZ</span>
                    <span className="text-[8px] text-indigo-500 font-bold uppercase tracking-[0.2em] -mt-1">Evaluation</span>
                  </div>
                </div>

                {/* Top Right: Green Tick & Status */}
                <div className="flex flex-col items-end">
                  <div className="relative">
                    <div className="absolute inset-0 bg-emerald-500 blur-lg opacity-40 animate-pulse"></div>
                    <ShieldCheckIcon className="h-10 w-10 text-emerald-500 relative z-10" />
                  </div>
                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mt-1">Verified</span>
                </div>
              </div>

              {/* MIDDLE ROW: CREDENTIAL & DATE SPLIT */}
              <div className="grid grid-cols-2 gap-8 border-y border-white/5 py-10 mb-8">
                {/* Left Side: Credential Type */}
                <div className="flex flex-col space-y-1">
                  <p className="text-[9px] uppercase font-black text-gray-500 tracking-[0.2em]">Credential Type</p>
                  <p className="text-sm font-black text-white italic uppercase leading-tight">
                    {certData.certificate_type || "Official Intern"}
                  </p>
                </div>

                {/* Right Side: Validation Date */}
                <div className="flex flex-col items-end space-y-1 text-right">
                  <p className="text-[9px] uppercase font-black text-gray-500 tracking-[0.2em]">Issued Date</p>
                  <p className="text-sm font-black text-white italic uppercase leading-tight">
                    {certData.issue_date ? new Date(certData.issue_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: '2-digit', 
                      year: 'numeric' 
                    }) : "N/A"}
                  </p>
                </div>
              </div>

              {/* BOTTOM ROW: SERIAL & BOARD */}
              <div className="flex justify-between items-end">
                <div className="flex flex-col space-y-1">
                   <p className="text-[8px] text-gray-600 uppercase font-black tracking-widest">Certificate Id </p>
                   <p className="text-[10px] font-mono text-indigo-400/80 uppercase tracking-tighter">#{serial}</p>
                </div>
                <div className="text-right">
                  <p className="text-[7px] text-gray-700 uppercase font-bold tracking-[0.3em]">Authorized by</p>
                  <p className="text-[9px] font-serif italic text-white/40">GenZ Evaluation Team</p>
                </div>
              </div>
            </div>
          ) : (
            <ErrorState onExit={() => navigate('/')} />
          )}
        </div>
      </div>

      <footer className="mt-8 opacity-20">
        <p className="text-[8px] font-black tracking-[0.8em] uppercase">© GenZ Evaluation Security Systems</p>
      </footer>
    </div>
  );
};

// Helper Components for Cleanliness
const LoadingScreen = () => (
  <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center text-white">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-500 mb-4"></div>
    <p className="text-gray-600 font-mono text-[9px] tracking-[0.5em] uppercase">Auth Node Syncing</p>
  </div>
);

const ErrorState = ({ onExit }) => (
  <div className="p-16 text-center">
    <ExclamationCircleIcon className="h-12 w-12 text-red-500/20 mx-auto mb-4" />
    <p className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-6">Access Denied: Invalid Key</p>
    <button onClick={onExit} className="text-[9px] text-indigo-500 font-bold uppercase border-b border-indigo-500/30 pb-1">Return Home</button>
  </div>
);

export default VerifyCertificate;